/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiI18n } from '../i18n';
import { useGeneratedHtmlId } from '../../services';
import { useEuiResizableContainerContext } from './context';
export var EuiResizableButton = ({
  isHorizontal,
  className,
  id,
  registration,
  disabled,
  onFocus,
  onBlur,
  ...rest
}) => {
  const resizerId = useGeneratedHtmlId({
    prefix: 'resizable-button',
    conditionalId: id
  });
  const {
    registry: {
      resizers
    } = {
      resizers: {}
    }
  } = useEuiResizableContainerContext();
  const isDisabled = useMemo(() => disabled || resizers[resizerId] && resizers[resizerId].isDisabled, [resizers, resizerId, disabled]);
  const classes = classNames('euiResizableButton', {
    'euiResizableButton--vertical': !isHorizontal,
    'euiResizableButton--horizontal': isHorizontal,
    'euiResizableButton--disabled': isDisabled
  }, className);
  const previousRef = useRef();
  const onRef = useCallback(ref => {
    if (!registration) return;

    if (ref) {
      previousRef.current = ref;
      registration.register({
        id: resizerId,
        ref,
        isFocused: false,
        isDisabled: disabled || false
      });
    } else {
      if (previousRef.current != null) {
        registration.deregister(resizerId);
        previousRef.current = undefined;
      }
    }
  }, [registration, resizerId, disabled]);

  const setFocus = e => e.currentTarget.focus();

  const handleFocus = () => {
    onFocus && onFocus(resizerId);
  };

  return <EuiI18n tokens={['euiResizableButton.horizontalResizerAriaLabel', 'euiResizableButton.verticalResizerAriaLabel']} defaults={['Press left or right to adjust panels size', 'Press up or down to adjust panels size']}>
      {([horizontalResizerAriaLabel, verticalResizerAriaLabel]) => <button id={resizerId} ref={onRef} aria-label={isHorizontal ? horizontalResizerAriaLabel : verticalResizerAriaLabel} className={classes} data-test-subj="euiResizableButton" type="button" onClick={setFocus} onFocus={handleFocus} onBlur={onBlur} disabled={isDisabled} {...rest} />}
    </EuiI18n>;
};
EuiResizableButton.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
export function euiResizableButtonWithControls(controls) {
  return props => <EuiResizableButton {...controls} {...props} />;
}

try {
  euiResizableButtonWithControls.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/resizable_container/resizable_button.tsx',
    description: '',
    displayName: 'euiResizableButtonWithControls',
    methods: [],
    props: {
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '(eve: EuiResizableButtonKeyDownEvent) => void'
        }
      },
      onMouseDown: {
        defaultValue: null,
        description: '',
        name: 'onMouseDown',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '(eve: EuiResizableButtonMouseEvent) => void'
        }
      },
      onTouchStart: {
        defaultValue: null,
        description: '',
        name: 'onTouchStart',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '(eve: EuiResizableButtonMouseEvent) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '(id: string) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      registration: {
        defaultValue: null,
        description: '',
        name: 'registration',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: '{ register: (resizer: EuiResizableButtonController) => void; deregister: (resizerId: string) => void; }'
        }
      },
      isHorizontal: {
        defaultValue: null,
        description: '',
        name: 'isHorizontal',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiResizableButtonControls', 'HTMLAttributes', 'ButtonHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}

try {
  EuiResizableButton.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/resizable_container/resizable_button.tsx',
    description: '',
    displayName: 'EuiResizableButton',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '(eve: EuiResizableButtonKeyDownEvent) => void'
        }
      },
      onMouseDown: {
        defaultValue: null,
        description: '',
        name: 'onMouseDown',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '(eve: EuiResizableButtonMouseEvent) => void'
        }
      },
      onTouchStart: {
        defaultValue: null,
        description: '',
        name: 'onTouchStart',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '(eve: EuiResizableButtonMouseEvent) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '(id: string) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      registration: {
        defaultValue: null,
        description: '',
        name: 'registration',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: '{ register: (resizer: EuiResizableButtonController) => void; deregister: (resizerId: string) => void; }'
        }
      },
      isHorizontal: {
        defaultValue: null,
        description: '',
        name: 'isHorizontal',
        parent: {
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        },
        declarations: [{
          fileName: 'app/src/components/resizable_container/resizable_button.tsx',
          name: 'EuiResizableButtonControls'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiResizableButtonControls', 'HTMLAttributes', 'ButtonHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}